import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import _ from 'lodash';
import StatusBarComponent from './StatusBarComponent.jsx';
import LoadingIndicator from '../common/loading_indicator.jsx';
import Toolbar from './Toolbar.jsx';
import HeaderComponent from './HeaderComponent.jsx';

import AgGrid, { AgGridWrapper, getLocaleText, getServerSideSelectedNodes } from '../common/AgGrid/index.jsx';
import { createDefaultDatagridExportOptions } from '../common/AgGrid/ExportFilename.jsx';
import useRescaleColumns from './hooks/useRescaleColumns.js';
import useDynamicCols, { useDynamicDefaultCols } from './hooks/useDynamicCols.jsx';
import useSidePanel from './hooks/useSidePanel.js';
import useServerSideData from './hooks/useServerSideData.jsx';
import useExitWarning from './hooks/useExitWarning.js';
import useCustomAggFuncs from './hooks/useCustomAggFuncs.js';
import usePivot from './hooks/usePivot.js';
import useToggleableStatusBar from './hooks/useToggleableStatusBar.js';
import { restoreDefaultPreset } from './Presets.jsx';
import ValidationCell from './ValidationCell.jsx';
import RowEditDialog from './RowEditDialog.jsx';
import { createRoot } from 'react-dom/client';
import { useSelectionColumnClick } from './hooks/useSelectionColumnClick.js';

const processCellForClipboard = (params) => {
  const {value, column: {colDef: {cellRendererParams}}} = params;
  
  // Allow columns to define how they are processed
  if (typeof cellRendererParams.processForClipboard == 'function') {
    return cellRendererParams.processForClipboard(params)
  }

  return value;
}

const Datagrid = React.forwardRef(({
  datagrid, 
  context, 
  wrapperStyle, 
  CustomToolbar, 
  supressToolbar = false,
  supressColumnConfiguration = false,
  supressBulkActions = false,
  supressSorting = false,
  agGridProps = {},
  displayTotals = false,
  colorColumnByHeaderGroup = false,
  ExternalFilter = null,
  enabledFilterColumns = [],
  hiddenFilterColumns = [],
  totalsConfiguration = null,
  externalFilterProps = {},
  externalFilterContainer = null,
  chartParams = null,
  hideTitle = false,
  addRowLabel = null
}, ref) => {

  const gridRef = ref || useRef(ref);
  const unsavedTransactions = useRef([]);
  const wrapperRef = useRef();
  const [editMode, setEditMode] = useState(false);
  const [dialogRow, setDialogRow] = useState(null);

  const rescaleColumns = useRescaleColumns(gridRef, wrapperRef);
  const initialColumnDefs = useDynamicCols(datagrid.columns || [], {
    editMode: editMode,
    restrictions: context?.permissions?.restrictions,
    canModifyNonEditableRows: context?.permissions?.modify_non_editable_rows,
    supressBulkActions: supressBulkActions || !context?.permissions?.edit,
    colorColumnByHeaderGroup: colorColumnByHeaderGroup
  });

  const components = useMemo(() => {
    return {
      agColumnHeader: HeaderComponent,
      dgValidationCell: ValidationCell
    };
  }, []);


  const defaultColDef = useDynamicDefaultCols(context, editMode, supressSorting);
  const sideBar = useSidePanel({context, toolPanelParams: {
    datagrid: datagrid, 
  }});
  const toggleStatusBarComp = useToggleableStatusBar(gridRef)
  const hasDefaultAggregate = useMemo(() =>  (datagrid.columns || []).some(c => c.default_aggregate === true), [datagrid.columns]);
  const {
    onSave,
    onDiscard,
    serverSideDatasource,
    addRow,
    removeSelectedRows,
    onCellEdited,
    getRowId,
    autoGroupColumnDef,
    getAllParentGroupsDataMap,
    addGroupedRowCallback,
    saveRowDialog
  } = useServerSideData(datagrid.id, context, gridRef, setEditMode, editMode, toggleStatusBarComp, displayTotals, chartParams, totalsConfiguration, unsavedTransactions, setDialogRow, hasDefaultAggregate);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: StatusBarComponent,
          key: 'statusBarCompKey',
          statusPanelParams : {
              onSave: () => onSave(),
              onDiscard: onDiscard
          }
        }
      ],
    };
  }, [onSave, onDiscard]);

  useEffect(()=> {
    gridRef.current?.api?.setGridOption('statusBar', statusBar);
  }, [editMode]);

  const loadingOverlayComponent = useMemo(() => {
    return LoadingIndicator;
  }, []);

  const ToolbarComponent = CustomToolbar ? CustomToolbar : Toolbar;

  const gridReady = (event) => {
    if (ExternalFilter && externalFilterContainer) {
      const root = createRoot(document.querySelector(externalFilterContainer));
      root.render(<ExternalFilter columnDefs={initialColumnDefs} onFilterChange={handleFilterChange} onHiddenColumnFilterChange={handleHiddenColumnFilterChange} {...externalFilterProps} />);
    }

    // timeout to ensure this runs before the next code executes
    setTimeout(() => {
      event.api.setGridOption('serverSideDatasource', serverSideDatasource);
    }, 0);

    if (typeof agGridProps.gridReady == 'function') agGridProps.gridReady(event);
    rescaleColumns();
    if (!context?.permissions?.configure) restoreDefaultPreset(datagrid, gridRef);
  }

  const onColumnPivotModeChanged = usePivot(gridRef);
  const getContextMenuItems = useCallback((params) => {
    var contextMenuItems = [];

    // Only show the "Add Row" option if we are in grouped and edit mode
    if (params.node?.group && editMode) {
      contextMenuItems = contextMenuItems.concat(
        [
          {
            name: addRowLabel || I18n.t('add_row'),
            action: () => {
              const selectedNode = params.node;
              const { data, groupKeys } = getAllParentGroupsDataMap(selectedNode);

              const rowData = [{[params.node.field]: params.node.data[params.node.field]}, ...data].reduce((acc, curr) => {
                return { ...acc, ...curr };
              }, {});

              addGroupedRowCallback(params,[...groupKeys, selectedNode.key], rowData);
            },
            icon: '<i class="fa fa-plus"></i>', // Add an icon
          },
          'separator', // Add a separator
        ]
      );
    }

    // Add other items
    contextMenuItems = contextMenuItems.concat(
      [
        'cut',
        'copy',
        'copyWithHeaders',
        'copyWithGroupHeaders',
        'paste',
        ...(
          (!editMode &&
            (params.node && !params.node.group) &&
            context?.permissions.single_row_edit &&
            context?.context_name === 'datagrid' &&
            (params.node.data.editable || context?.permissions.modify_non_editable_rows)
          ) ? [{
          name: I18n.t('edit_row'),
          action: () => {
            setDialogRow(params.node.data);
          },
          icon: '<i class="fa fa-edit" style="margin-left: 2px;"></i>'
        }] : []),
      ]
    );

    return contextMenuItems;
  }, [editMode]);

  const aggFuncs = useCustomAggFuncs(datagrid.columns, initialColumnDefs);
  useExitWarning(editMode, setEditMode);

  const handleFilterChange = filters => {
    gridRef.current.api.setFilterModel(filters);
  };

  const handleHiddenColumnFilterChange = (filters) => {
    gridRef.current.api.setGridOption('context', {...context, get_rows_params: {...(context.get_rows_params || {}), hiddenColumnFilters: filters}});
    gridRef.current.api.onFilterChanged();
  };

  // Opening groups automatically since SSRM requires the rows to be loaded in order to be selected
  const autoExpandSelectedChildren = params => {
    const selectedNodes = getServerSideSelectedNodes(params).filter(node => {
      return node.group && !node.expanded && !node.childStore
    });
    selectedNodes.forEach(node => {
      params.api.setRowNodeExpanded(node, true);
    });
  }

  const rowSelection = useMemo(() => { 
    return {
      mode: 'multiRow',
      groupSelects: 'descendants',
      hideDisabledCheckboxes: true,
      checkboxes: true,
      headerCheckbox: false,
      enableClickSelection: false,
      selectAll: "filtered",
      isRowSelectable: (rowNode) => {
        return (rowNode.data?.editable !== false);
      }
    };
  }, []);

  const getCustomMainMenuItems = (params) => {
    const defaultItems = params.defaultItems;
    return [
        ...defaultItems,
      'separator',
      {
        name: I18n.t('copy', {scope: 'ag_translations'}),
        action: () => {
          const colName = params.columnGroup?.colGroupDef?.headerName || params.column.getColDef().headerName;
          navigator.clipboard.writeText(colName).catch(err => console.error(err));
        },
        icon: `<span class="ag-icon ag-icon-copy"></span>`,
      }
    ];
  }

  const { gridReady: unused, ...gridProps } = agGridProps;

  return (
    <AgGridWrapper 
      ref={wrapperRef}
      wrapperStyle={{
        height: 'calc(100vh - 56px - 56px - 1rem)', // Viewport height - topbar - monitoring header - bottom margin
        ...wrapperStyle        
      }}
      className={editMode ? 'datagrid-edit-mode' : ''}
    >
      {dialogRow && <RowEditDialog
        rowData={dialogRow}
        setDialogRow={setDialogRow}
        initialColumnDefs={initialColumnDefs}
        unsavedTransactions={unsavedTransactions}
        saveRowDialog={saveRowDialog}
        singleRowAdd={context?.singleRowAdd || {}}
      />}
      {!supressToolbar && <ToolbarComponent 
        editMode={editMode} 
        setEditMode={setEditMode} 
        gridRef={gridRef} 
        addRow={addRow} 
        removeSelectedRows={removeSelectedRows}
        datagrid={datagrid}
        toggleStatusBarComp={toggleStatusBarComp}
        context={context}
        hideTitle={hideTitle}
        addRowLabel={addRowLabel}
        setDialogRow={setDialogRow}
      />}
      <AgGrid
          ref={gridRef} // Ref for accessing Grid's API
          columnDefs={initialColumnDefs}
          containerStyle={{flex: 1}}
          context={context}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          animateRows={true}
          rowSelection={rowSelection}
          getLocaleText={getLocaleText}
          aggFuncs={aggFuncs}
          sideBar={sideBar}
          rowModelType={'serverSide'}
          getRowId={getRowId}
          readOnlyEdit={true}
          onCellEditRequest={onCellEdited}
          onCellValueChanged={onCellEdited}
          statusBar={statusBar}
          cellSelection={true}
          loadingOverlayComponent={loadingOverlayComponent}
          groupHeaderHeight={20}
          {...gridProps}
          onGridReady={gridReady}
          onColumnPivotModeChanged={onColumnPivotModeChanged}
          stopEditingWhenCellsLoseFocus
          gridOptions = {{...createDefaultDatagridExportOptions(datagrid.name), singleClickEdit: true}}
          onCellClicked={useSelectionColumnClick()}
          components = {components}
          getContextMenuItems={getContextMenuItems}
          rowGroupPanelShow={false}
          rowHeight={38}
          groupDisplayType='multipleColumns'
          getChildCount={data => {
            return data ? data.childCount : undefined
          }}
          processCellForClipboard={processCellForClipboard}
          reactiveCustomComponents={true}
          selectionColumnDef={{
            width: 50,
            hide: supressBulkActions || !context?.permissions?.edit,
            resizable: true,
          }}
          getMainMenuItems={getCustomMainMenuItems}
          onSelectionChanged={autoExpandSelectedChildren}
          onStoreRefreshed={autoExpandSelectedChildren}
          grandTotalRow={hasDefaultAggregate ? 'bottom' : undefined}
        />
    </AgGridWrapper>
  )

});

export default Datagrid;
